import { useCustom } from "@refinedev/core";
import { AlertTriangle, ChevronDown, ChevronRight, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { PeriodPicker, type PeriodPreset } from "@/components/PeriodPicker";
import { SpisanieFeed } from "@/components/SpisanieFeed";
import { query } from "@/lib/api";
import { iso, lastDays } from "@/lib/dates";
import { errorMessage } from "@/lib/format";
import type {
  SpisanieSummary,
  SpisanieSummaryLines,
  SummaryCity,
  SummaryLine,
} from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Период по умолчанию — неделя (владелец 29.09.26); его же считает сервис. */
const DEFAULT_DAYS = 7;

/** Строк детализации за раз и потолок — те же, что у сервиса. */
const PAGE = 200;
const LIMIT_MAX = 1000;

const PRESETS: readonly PeriodPreset[] = [
  { days: 7, label: "7 дней", hint: "последняя неделя — по умолчанию" },
  { days: 30, label: "30 дней", hint: "последний месяц" },
  { days: 90, label: "90 дней", hint: "последние три месяца" },
  { days: 365, label: "год", hint: "последние двенадцать месяцев" },
];

type Side = "out" | "in";

const SIDE_NAME: Record<Side, string> = { out: "Списано", in: "Оприходовано" };

/** Город в адресе: номер или `none` — «город неизвестен». */
const cityKey = (c: SummaryCity) => String(c.city_id ?? "none");

/** Причина в адресе: номер, `none` — без причины, `fbs` — излишек FBS. */
const lineKey = (l: SummaryLine) =>
  l.source === "fbs" ? "fbs" : String(l.category_id ?? "none");

/**
 * Раздел «ОПРИХОДОВАНИЕ И СПИСАНИЕ» — сколько склад списал и оприходовал в
 * каждом городе за период (задача владельца 29.09.26), и какие вещи за этим
 * стоят.
 *
 * «Оприходование», а не «приход»: приходом на складе зовут поставку и
 * приёмку, а их здесь нет — только учётные поправки остатка (снятие списания
 * и излишек инвентаризации FBS).
 *
 * Вопрос не журнала, а ИТОГА: журнал `/spisanie` отвечает построчно, здесь —
 * число на город и его разбивка по причинам. Поток маркетплейса (отгрузка
 * FBS/FBW и возврат с площадки) исключён целиком — список исключённого назван
 * под пикером, иначе «списано 330» читалось бы как всё, что ушло со склада.
 *
 * КАЖДАЯ ЦИФРА — КНОПКА (разметка владельца 29.09.26): под таблицей
 * открываются вещи, из которых она сложена, — серия, товар, место, кто и
 * когда. Строки рисует тот же `SpisanieFeed`, что журнал: операция та же.
 *
 * Период и выбранная цифра живут В АДРЕСЕ (`?from=`, `?to=`, `?side=`,
 * `?city=`, `?category=`): ссылку на находку отправляют, и открыться она
 * обязана той же. Пустой период — последние 7 дней.
 */
export function SpisanieSummaryPage() {
  const [params, setParams] = useSearchParams();
  const fromParam = params.get("from") ?? "";
  const toParam = params.get("to") ?? "";
  const sideParam = params.get("side");
  const side: Side | null = sideParam === "out" || sideParam === "in" ? sideParam : null;
  const cityParam = params.get("city") ?? "";
  const categoryParam = params.get("category") ?? "";
  const limitParam = params.get("n") ?? "";
  // Город выбранной цифры раскрыт сразу: по ссылке на строку причины человек
  // обязан видеть, в какой строке он стоит.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(categoryParam && cityParam ? [cityParam] : []),
  );

  const url = useMemo(
    () =>
      `/api/spisanie/summary${query({ from: fromParam || undefined, to: toParam || undefined })}`,
    [fromParam, toParam],
  );
  const got = useCustom<SpisanieSummary>({
    url,
    method: "get",
    queryOptions: { placeholderData: (prev) => prev },
  });

  const loaded = got.result.data;
  // `result.data` у Refine есть и до загрузки — пустым объектом.
  const data = loaded && Array.isArray(loaded.cities) ? loaded : null;
  const busy = got.query.isFetching;
  const error = got.query.error;
  useSessionGuard(error);

  const [defFrom, defTo] = lastDays(DEFAULT_DAYS);
  const from = data?.from ?? (fromParam || defFrom);
  const to = data?.to ?? (toParam || defTo);

  const pick = (nextFrom: string, nextTo: string) => {
    const next = new URLSearchParams(params);
    // Неделя по сегодня — это умолчание: адрес чистится, чтобы ссылка не
    // застывала на сегодняшних датах.
    if (nextFrom === defFrom && nextTo === defTo) {
      next.delete("from");
      next.delete("to");
    } else {
      next.set("from", nextFrom);
      next.set("to", nextTo);
    }
    next.delete("n");
    setParams(next, { replace: true });
  };

  /** Выбрать цифру: сторона, город (пусто — все), причина (пусто — все).
   * Повторный щелчок по той же цифре — закрыть детализацию. */
  const choose = (nextSide: Side | null, city = "", category = "") => {
    const next = new URLSearchParams(params);
    const same =
      nextSide === side && city === cityParam && category === categoryParam;
    for (const k of ["side", "city", "category", "n"]) next.delete(k);
    if (nextSide && !same) {
      next.set("side", nextSide);
      if (city) next.set("city", city);
      if (category) next.set("category", category);
    }
    setParams(next, { replace: true });
  };

  const isChosen = (s: Side, city = "", category = "") =>
    side === s && cityParam === city && categoryParam === category;

  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const cities = data?.cities ?? [];

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="SpisanieSummaryPage"
      data-annot="оприходование и списание"
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
          Оприходование и списание
        </h1>
        <span className="text-[13px] text-fg-muted">
          {busy && !data ? "загружаю…" : `${from} … ${to}`}
          {busy && data ? " · обновляю…" : ""}
        </span>
      </header>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <PeriodPicker
          label="период"
          annot="период"
          from={from}
          to={to}
          since={null}
          days={data?.days ?? DEFAULT_DAYS}
          max={iso(new Date())}
          presets={PRESETS}
          hint={`оприходование и списание за ${from} … ${to}`}
          onChange={pick}
        />
        {data?.excluded.length ? (
          <span className="text-[12.5px] text-fg-muted" data-annot="исключено">
            без потока маркетплейса: {data.excluded.map((e) => e.name).join(", ")}
          </span>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">{errorMessage(error)}</p>
        </div>
      ) : cities.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {busy ? "Загружаю…" : "За период не списывали и не оприходовали ничего."}
        </div>
      ) : (
        <div
          // `shrink-0`: блок с `overflow-auto` в колонке flex ужимается до нуля,
          // как только ниже открывается длинная детализация.
          className="shrink-0 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle md:max-w-[640px]"
          data-annot="города"
        >
          <table className="w-full border-collapse text-[13px]">
            <caption className="sr-only">
              Оприходование и списание по городам за период
            </caption>
            <thead>
              <tr>
                <th scope="col" className={TH + " text-left"}>
                  Город
                </th>
                <th scope="col" className={TH + " text-right"}>
                  Списано
                </th>
                <th scope="col" className={TH + " text-right"}>
                  Оприходовано
                </th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => {
                const k = cityKey(c);
                const shown = open.has(k);
                return (
                  <Fragment key={k}>
                    <tr
                      className="cursor-pointer border-b border-border hover:bg-bg-hover"
                      onClick={() => toggle(k)}
                      data-annot="город"
                    >
                      <td className="px-2.5 py-2">
                        <button
                          type="button"
                          aria-expanded={shown}
                          className="flex items-center gap-1.5 text-left"
                        >
                          {shown ? (
                            <ChevronDown size={13} className="text-fg-muted" />
                          ) : (
                            <ChevronRight size={13} className="text-fg-muted" />
                          )}
                          <span className={c.city_id === null ? "text-fg-muted italic" : ""}>
                            {c.city}
                          </span>
                        </button>
                      </td>
                      <Num
                        value={c.spisano}
                        chosen={isChosen("out", k)}
                        title={`Списано · ${c.city} — какие вещи`}
                        onPick={() => choose("out", k)}
                      />
                      <Num
                        value={c.oprihod}
                        chosen={isChosen("in", k)}
                        title={`Оприходовано · ${c.city} — какие вещи`}
                        onPick={() => choose("in", k)}
                      />
                    </tr>
                    {shown
                      ? c.lines.map((l) => {
                          const lk = lineKey(l);
                          return (
                            <tr
                              key={`${l.side}-${lk}`}
                              className="border-b border-border bg-bg text-[12.5px] text-fg-muted"
                              data-annot="причина"
                            >
                              <td className="py-1.5 pr-2.5 pl-9">{l.name}</td>
                              <Num
                                value={l.side === "out" ? l.qty : 0}
                                quiet
                                chosen={isChosen("out", k, lk)}
                                title={`Списано · ${c.city} · ${l.name} — какие вещи`}
                                onPick={() => choose("out", k, lk)}
                              />
                              <Num
                                value={l.side === "in" ? l.qty : 0}
                                quiet
                                chosen={isChosen("in", k, lk)}
                                title={`Оприходовано · ${c.city} · ${l.name} — какие вещи`}
                                onPick={() => choose("in", k, lk)}
                              />
                            </tr>
                          );
                        })
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="px-2.5 py-2">Итого</td>
                <Num
                  value={data?.spisano ?? 0}
                  chosen={isChosen("out")}
                  title="Всё списанное за период — какие вещи"
                  onPick={() => choose("out")}
                />
                <Num
                  value={data?.oprihod ?? 0}
                  chosen={isChosen("in")}
                  title="Всё оприходованное за период — какие вещи"
                  onPick={() => choose("in")}
                />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {side && data ? (
        <Detail
          data={data}
          side={side}
          city={cityParam}
          category={categoryParam}
          limit={Math.min(Number(limitParam) || PAGE, LIMIT_MAX)}
          onMore={(n) => {
            const next = new URLSearchParams(params);
            next.set("n", String(n));
            setParams(next, { replace: true });
          }}
          onClose={() => choose(null)}
        />
      ) : null}
    </main>
  );
}

/**
 * Вещи за выбранной цифрой: серия, товар, место, причина, кто и когда.
 *
 * Заголовок повторяет ЦИФРУ и её адрес («Списано · Ростов · Неликвид — 12»):
 * без него таблица ниже не говорит, на какой вопрос отвечает, а счёт строк
 * сверяется с цифрой глазами.
 */
function Detail({
  data,
  side,
  city,
  category,
  limit,
  onMore,
  onClose,
}: {
  data: SpisanieSummary;
  side: Side;
  city: string;
  category: string;
  limit: number;
  onMore: (n: number) => void;
  onClose: () => void;
}) {
  const url = useMemo(
    () =>
      `/api/spisanie/summary/lines${query({
        side,
        from: data.from,
        to: data.to,
        city: city || undefined,
        category: category || undefined,
        n: limit,
      })}`,
    [side, data.from, data.to, city, category, limit],
  );
  const got = useCustom<SpisanieSummaryLines>({
    url,
    method: "get",
    queryOptions: { placeholderData: (prev) => prev },
  });
  const loaded = got.result.data;
  const lines = loaded && Array.isArray(loaded.rows) ? loaded : null;
  const busy = got.query.isFetching;
  const error = got.query.error;
  useSessionGuard(error);

  const c = city ? data.cities.find((one) => cityKey(one) === city) : undefined;
  const l = c && category ? c.lines.find((one) => one.side === side && lineKey(one) === category) : undefined;
  const total = l
    ? l.qty
    : c
      ? side === "out"
        ? c.spisano
        : c.oprihod
      : side === "out"
        ? data.spisano
        : data.oprihod;
  const title = [SIDE_NAME[side], c ? c.city : "все города", l?.name]
    .filter(Boolean)
    .join(" · ");
  const shown = lines?.rows.length ?? 0;

  return (
    <section className="flex shrink-0 flex-col gap-2" data-annot="детализация">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[16px] font-semibold">
          {title} — <span className="tnum">{total.toLocaleString("ru-RU")}</span>
        </h2>
        <span className="text-[12.5px] text-fg-muted">
          {busy && !lines
            ? "загружаю…"
            : shown < total
              ? `показаны последние ${shown.toLocaleString("ru-RU")}`
              : "свежие сверху"}
          {busy && lines ? " · обновляю…" : ""}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть детализацию"
          className="ml-auto flex items-center gap-1 rounded-[var(--radius-ui)] px-2 py-1 text-[12.5px] text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <X size={13} /> закрыть
        </button>
      </header>

      {error ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-4 text-center text-[13.5px]"
        >
          {errorMessage(error)}
        </div>
      ) : lines && lines.rows.length ? (
        <SpisanieFeed
          rows={lines.rows}
          caption={title}
          withCard
          withPlace
          // Причина — колонкой, только когда цифра не одной причины.
          withCategory={!category}
          placeHeader={side === "out" ? "Откуда списали" : "Куда оприходовали"}
        />
      ) : (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-center text-[13px] text-fg-muted">
          {busy ? "Загружаю…" : "Строк нет."}
        </div>
      )}

      {lines?.has_more && limit < LIMIT_MAX ? (
        <button
          type="button"
          onClick={() => onMore(Math.min(limit + PAGE, LIMIT_MAX))}
          disabled={busy}
          data-annot="ещё"
          className="w-fit rounded-[var(--radius-ui)] border border-border px-3 py-1.5 text-[13px] hover:bg-bg-hover"
        >
          {busy ? "Загружаю…" : `Ещё ${Math.min(PAGE, LIMIT_MAX - limit)}`}
        </button>
      ) : null}
      {lines?.has_more && limit >= LIMIT_MAX ? (
        <p className="text-[12.5px] text-fg-muted">
          Показана тысяча последних строк — для остальных сузьте период.
        </p>
      ) : null}
    </section>
  );
}

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

/** Число ячейки. Ноль — прочерком: в строке причины одна из сторон всегда пуста,
 * и столбец нулей читался бы как «проверили — ничего».
 *
 * Не ноль — КНОПКА: щелчок открывает вещи, из которых число сложено. Щелчок
 * не доходит до строки города — иначе он заодно сворачивал бы её. */
function Num({
  value,
  quiet = false,
  chosen = false,
  title,
  onPick,
}: {
  value: number;
  quiet?: boolean;
  chosen?: boolean;
  title: string;
  onPick: () => void;
}) {
  return (
    <td className={"tnum px-1.5 text-right " + (quiet ? "py-0.5" : "py-1")}>
      {value ? (
        <button
          type="button"
          title={title}
          aria-pressed={chosen}
          data-annot="цифра"
          onClick={(e) => {
            e.stopPropagation();
            onPick();
          }}
          className={
            "tnum rounded-[var(--radius-ui)] px-1.5 py-1 underline decoration-border underline-offset-2 hover:bg-bg-hover hover:decoration-fg " +
            (chosen ? "bg-bg-hover font-semibold text-fg ring-1 ring-border-strong" : "")
          }
        >
          {value.toLocaleString("ru-RU")}
        </button>
      ) : (
        <span className="px-1.5 text-fg-muted">—</span>
      )}
    </td>
  );
}
